import { useRef, useCallback, useEffect, type ReactNode } from 'react';
import { useGT } from 'gt-react';
import { Camera, PaperclipIcon, X } from '@phosphor-icons/react';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import {
  ChatInputAttachments,
  type ChatInputAttachment,
} from './ChatInputAttachments';
import { cn } from '@/lib/utils';
import { VoiceInputButton } from './VoiceInputButton';
import { ChatInputActions, QueueHint } from './ChatInputActions';
import { composerKeyAction } from './composerRules';

export type { ChatInputAttachment } from './ChatInputAttachments';

interface ChatInputProps {
  value: string;
  onChange: (value: string) => void;
  onSend: (messageOverride?: string) => void;
  isLoading: boolean;
  onCancel?: () => void;
  placeholder?: string;
  className?: string;
  textareaClassName?: string;
  size?: 'sm' | 'default';
  /** Auto-focus the input when this becomes true */
  autoFocus?: boolean;
  /** Toggle screen sharing mode */
  onShareToggle?: () => void;
  /** Whether screen sharing is currently active */
  isShareActive?: boolean;
  /** Annotation toolbar rendered inside the screenshot info block */
  annotationToolbar?: ReactNode;
  attachments?: ChatInputAttachment[];
  onAttachFile?: () => void;
  onRemoveAttachment?: (documentId: string) => void;
  onRetryAttachment?: (documentId: string) => void;
  sendDisabled?: boolean;
  /** Extra control rendered in the composer-left button group (e.g. a mic). */
  leftSlot?: ReactNode;
  /**
   * Rendered in place of the text box, keeping the rest of the composer
   * (attach, capture, slots) — e.g. a waveform during a voice call.
   */
  inputReplacement?: ReactNode;
  /** Hide Send/Stop, e.g. on a voice call, where speaking sends. */
  hideSendAction?: boolean;
  /** Show the hold-to-speak mic button */
  enableVoiceInput?: boolean;
  /**
   * Called with the live partial transcript while recording, and with `null`
   * when a recording session ends. Wire it to mirror the transcript into
   * `value`.
   */
  onVoiceTranscription?: (text: string | null) => void;
  /**
   * Keep the composer usable while a reply streams (AI-518): Enter queues
   * the message to run after the reply, and Mod+Enter calls `onSteer` to
   * slot it in after the reply's current step.
   */
  queueWhileLoading?: boolean;
  onSteer?: () => void;
}

export function ChatInput({
  value,
  onChange,
  onSend,
  isLoading,
  onCancel,
  placeholder,
  className,
  textareaClassName,
  size = 'default',
  autoFocus = false,
  onShareToggle,
  isShareActive = false,
  annotationToolbar,
  attachments,
  onAttachFile,
  onRemoveAttachment,
  onRetryAttachment,
  sendDisabled = false,
  leftSlot,
  inputReplacement,
  hideSendAction = false,
  enableVoiceInput = false,
  onVoiceTranscription,
  queueWhileLoading = false,
  onSteer,
}: ChatInputProps) {
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const gt = useGT();
  const resolvedPlaceholder =
    placeholder ??
    gt('Type a message...', { context: 'chat input placeholder' });

  useEffect(() => {
    if (autoFocus) {
      textareaRef.current?.focus();
    }
  }, [autoFocus]);

  const canSend = !sendDisabled && (isShareActive || !!value.trim());
  const queueing = queueWhileLoading && isLoading;

  const handleSend = useCallback(
    (messageOverride?: string) => {
      if (!(messageOverride ?? value).trim() && !isShareActive) return;
      if ((isLoading && !queueWhileLoading) || sendDisabled) return;
      onSend(messageOverride);
      setTimeout(() => textareaRef.current?.focus(), 0);
    },
    [value, isLoading, isShareActive, onSend, sendDisabled, queueWhileLoading],
  );

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
      const action = composerKeyAction(e, queueing, !!onSteer);
      if (action === 'none') return;
      e.preventDefault();
      if (action === 'send') handleSend();
      else if (value.trim() && !sendDisabled) onSteer?.();
    },
    [handleSend, queueing, onSteer, value, sendDisabled],
  );

  const sizeStyles = {
    sm: {
      textarea: 'min-h-[50px] max-h-[80px] text-sm',
      icon: 20,
    },
    default: {
      textarea: 'min-h-[60px] max-h-[120px]',
      icon: 24,
    },
  };

  const styles = sizeStyles[size];
  const hasAttachments = attachments && attachments.length > 0;
  // The footer bar only earns its space when it holds left-side actions.
  // Without any (e.g. a text-only composer), we drop the bar and float the
  // send button in the textarea's corner instead of leaving an empty strip.
  const hasFooterActions =
    Boolean(onAttachFile) ||
    Boolean(onShareToggle) ||
    Boolean(leftSlot) ||
    enableVoiceInput;

  const actionButton = hideSendAction ? null : (
    <ChatInputActions
      isLoading={isLoading}
      queueing={queueing}
      canSend={canSend}
      onSend={() => handleSend()}
      onCancel={onCancel}
    />
  );

  return (
    <div className={cn('flex flex-col', className)}>
      {isShareActive && (
        <div className="rounded-t-md border border-b-0 bg-muted overflow-hidden">
          {annotationToolbar}
          <div className="flex items-center justify-between px-2.5 py-1.5 text-xs text-muted-foreground">
            <span>
              {gt('A screenshot will be included with your message', {
                context:
                  'info text shown when screen capture is active in chat',
              })}
            </span>
            {onShareToggle && (
              <button
                onClick={onShareToggle}
                className="ml-2 rounded p-0.5 hover:bg-accent transition-colors"
              >
                <X className="h-3 w-3" />
              </button>
            )}
          </div>
        </div>
      )}
      <div
        className={cn(
          'relative rounded-md border bg-background overflow-hidden',
          isShareActive && 'rounded-t-none border-t-0',
        )}
      >
        {hasAttachments && (
          <ChatInputAttachments
            attachments={attachments}
            onRetry={onRetryAttachment}
            onRemove={onRemoveAttachment}
          />
        )}
        {inputReplacement ?? (
          <Textarea
            ref={textareaRef}
            value={value}
            onChange={(e) => onChange(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder={resolvedPlaceholder}
            className={cn(
              'resize-none border-0 shadow-none focus-visible:ring-0',
              !hasFooterActions && 'pr-12',
              styles.textarea,
              textareaClassName,
            )}
          />
        )}
        {hasFooterActions ? (
          <div className="flex items-center justify-between px-2 py-1.5 border-t bg-muted/50">
            <div className="flex items-center gap-0.5">
              {leftSlot}
              {enableVoiceInput && (
                <VoiceInputButton
                  disabled={isLoading}
                  onTranscript={onVoiceTranscription}
                  onCommit={handleSend}
                />
              )}
              {onAttachFile && (
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  onClick={onAttachFile}
                  disabled={isLoading}
                  className="h-7 w-7 p-0"
                  aria-label={gt('Attach file', {
                    context: 'attach file to chat message button',
                  })}
                >
                  <PaperclipIcon className="h-4 w-4 text-stone-500" />
                </Button>
              )}
              {onShareToggle && (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={onShareToggle}
                  className={cn(
                    'h-7 w-7 p-0',
                    isShareActive && 'bg-accent text-accent-foreground',
                  )}
                  title={
                    isShareActive
                      ? gt('Stop screen capture', {
                          context: 'button tooltip to stop screen capture',
                        })
                      : gt('Capture screen', {
                          context: 'button tooltip to start screen capture',
                        })
                  }
                >
                  <Camera
                    className="h-4 w-4"
                    weight={isShareActive ? 'fill' : 'regular'}
                  />
                </Button>
              )}
            </div>
            {actionButton}
          </div>
        ) : (
          <div className="absolute bottom-2 right-2">{actionButton}</div>
        )}
      </div>
      {queueing && <QueueHint canSteer={!!onSteer} />}
    </div>
  );
}
